import { detectDevice } from 'Utils';
import type { DeviceKind } from './Constants';

export type DeviceClass = 'phone' | 'tablet' | 'desktop' | 'unknown';
export type RouteLayout = 'auth' | 'app' | 'other';
export type OrientationMode = 'portrait' | 'landscape' | 'system';

export const AUTH_ROUTE_PREFIXES = [
  '/signin',
  '/register',
  '/verify',
  '/welcome',
  '/trial-welcome',
  '/sound-output',
];

export const APP_LANDSCAPE_ROUTE_PREFIXES = [
  '/roadmap',
  '/tutorial',
  '/lesson',
  '/settings',
  '/repertoire',
  '/repertoire-play',
  '/dashboard',
  '/plan',
];

const getUserAgent = () => {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
};

const matchesRoutePrefix = (pathname: string, prefix: string) => {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
};

const getRouteSegment = (pathname: string) => {
  const normalizedPathname = pathname.replace(/^\/+/, '');
  const [segment] = normalizedPathname.split('/');
  return segment || 'root';
};

export const getRouteLayout = (pathname: string): RouteLayout => {
  if (AUTH_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(pathname, prefix))) {
    return 'auth';
  }
  if (APP_LANDSCAPE_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(pathname, prefix))) {
    return 'app';
  }
  return 'other';
};

export const getDeviceClass = (deviceKind: DeviceKind = detectDevice()): DeviceClass => {
  if (deviceKind === 'iphone') return 'phone';
  if (deviceKind === 'ipad') return 'tablet';
  if (deviceKind === 'desktop' || deviceKind === 'web') return 'desktop';
  if (deviceKind === 'android') {
    const ua = getUserAgent();
    // Android tablets usually omit "Mobile" in the UA string.
    return /Mobile/i.test(ua) ? 'phone' : 'tablet';
  }
  return 'unknown';
};

export const getOrientationModeForRoute = (pathname: string): OrientationMode => {
  const routeLayout = getRouteLayout(pathname);
  if (routeLayout === 'auth') return 'portrait';
  if (routeLayout === 'app') return 'landscape';
  return 'system';
};

export const syncResponsiveDataAttributes = (pathname: string, deviceClass: DeviceClass) => {
  if (typeof document === 'undefined') return;
  const routeLayout = getRouteLayout(pathname);
  document.documentElement.setAttribute('data-device-class', deviceClass);
  document.documentElement.setAttribute('data-route-layout', routeLayout);
  document.documentElement.setAttribute('data-route-segment', getRouteSegment(pathname));
};
